// src/pages/pmc/PmcPacsApplicationsPane.js

import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { db } from "../../firebaseConfig";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import jsPDF from "jspdf";

const cx = (...xs) => xs.filter(Boolean).join(" ");

const CAPACITY_OPTIONS = [500, 750, 1000, 1500, 2000];

function clean(v = "") {
  return String(v || "").trim().toLowerCase();
}

function formatDate(value) {
  if (!value) return "—";
  try {
    const d = value?.toDate ? value.toDate() : new Date(value);
    return dayjs(d).format("DD/MM/YYYY");
  } catch {
    return "—";
  }
}

function formatDateTime(value) {
  if (!value) return "—";
  try {
    const d = value?.toDate ? value.toDate() : new Date(value);
    return dayjs(d).format("DD/MM/YYYY hh:mm A");
  } catch {
    return "—";
  }
}

function normalizeRow(row = {}) {
  return {
    ...row,
    applicationId: String(row.applicationId || row.projectId || row.id || "").trim(),
    pacsName: String(
      row.pacsName || row.societyName || row.organizationName || row.applicantName || ""
    ).trim(),
    pacsRegistrationNo: String(
      row.pacsRegistrationNo || row.registrationNumber || row.registrationNo || ""
    ).trim(),
    contactName: String(
      row.contactName || row.authorisedPersonName || row.name || ""
    ).trim(),
    contactPhone: String(
      row.contactPhone || row.mobile || row.phone || row.contactMobile || ""
    ).trim(),
    contactEmail: String(row.contactEmail || row.email || "").trim(),
    state: String(
      row.state || row.stateName || row.projectState || row.pacsState || ""
    ).trim(),
    district: String(
      row.district ||
        row.districtName ||
        row.projectDistrict ||
        row.pacsDistrict ||
        ""
    ).trim(),
    taluka: String(row.taluka || row.block || row.pacsTaluka || "").trim(),
    village: String(row.village || row.location || row.pacsVillage || "").trim(),
    capacityMt: Number(
      row.capacityMt || row.proposedCapacityMt || row.capacityMT || row.capacity || row.godownCapacity || 0
    ) || null,
    godownType: String(
      row.godownType || row.storageType || row.structureType || "CONVENTIONAL"
    ).trim(),
    plotLength: Number(row.plotLength || row.landLength || 0) || 0,
    plotWidth: Number(row.plotWidth || row.landWidth || 0) || 0,
    landArea: Number(row.landArea || row.landAreaSqM || 0) || 0,
  };
}

function isStrictNafedApply(row = {}) {
  return (
    row.nafedProject === true &&
    row.projectSource === "NAFED" &&
    row.applicationPortal === "NAFED_PACS_APPLY"
  );
}

function applyNafedScopeFilter(list = [], nafedScope = {}) {
  const scopeState = clean(nafedScope?.state);

  const allowedDistricts = (
    nafedScope?.allowedDistricts ||
    nafedScope?.districts ||
    []
  ).map(clean);

  return list.filter((row) => {
    const stateOk = !scopeState || clean(row.state) === scopeState;
    const districtOk =
      !allowedDistricts.length || allowedDistricts.includes(clean(row.district));

    return isStrictNafedApply(row) && row.isDeleted !== true && row.archived !== true && stateOk && districtOk;
  });
}

function Badge({ type = "pending", children }) {
  const cls = {
    pending: "badge pending",
    approved: "badge approved",
    rejected: "badge rejected",
    info: "badge info",
    neutral: "badge neutral",
  };

  return <span className={cls[type] || cls.neutral}>{children}</span>;
}

function StatCard({ label, value }) {
  return (
    <div className="stat-card">
      <label>{label}</label>
      <b>{value}</b>
    </div>
  );
}

function downloadPdfReport(
  rows,
  title = "NAFED PACS Applications",
  documentType = "REPORT"
) {
  const list = rows.map(normalizeRow);
  // IMPORTANT: document layout must be selected by the clicked action,
  // never by the number of records. A report with one record is still a report.
  const single = documentType === "LETTER";
  if (!list.length) {
    window.alert("No application is available for this report.");
    return;
  }
  const pdf = new jsPDF(single ? "p" : "l", "pt", "a4");
  const margin = 48;
  const navy = [20, 25, 32];
  const green = [20, 25, 32];
  const border = [145, 152, 162];

  const header = (pageNo) => {
    const width = pdf.internal.pageSize.getWidth();
    if (single) {
      // Intentionally blank letterhead area for printing on official stationery.
      pdf.setFillColor(255, 255, 255);
      pdf.rect(0, 0, width, 112, "F");
      return;
    }
    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, 0, width, 72, "F");
    pdf.setDrawColor(25, 30, 38);
    pdf.setLineWidth(1.2);
    pdf.line(margin, 63, width - margin, 63);
    pdf.setTextColor(...green);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(13);
    pdf.text("NAFED", margin, 28);
    pdf.setTextColor(20, 25, 32);
    pdf.setFontSize(12);
    pdf.text(title, width / 2, 28, { align: "center" });
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7.5);
    pdf.text("Grain Storage Digital Project Monitoring System", width / 2, 43, { align: "center" });
    pdf.text(`Generated: ${dayjs().format("DD/MM/YYYY hh:mm A")}`, width - margin, 27, { align: "right" });
    pdf.text(`Page ${pageNo}`, width - margin, 43, { align: "right" });
  };

  const footer = () => {
    const width = pdf.internal.pageSize.getWidth();
    const height = pdf.internal.pageSize.getHeight();
    if (single) {
      // Official PACS letter: no portal/software branding in the footer.
      pdf.setTextColor(95, 95, 95);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7);
      pdf.text(`Page ${pdf.getCurrentPageInfo().pageNumber}`, width / 2, height - 24, {
        align: "center",
      });
      return;
    }
    pdf.setDrawColor(...border);
    pdf.line(margin, height - 34, width - margin, height - 34);
    pdf.setTextColor(105, 116, 130);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(6.5);
    pdf.text("System-generated project report for authorised administrative use.", margin, height - 21);
    pdf.text("NAFED Grain Storage Digital Portal", width - margin, height - 21, { align: "right" });
  };

  if (single) {
    const r = list[0];
    header(1);
    const pageWidth = pdf.internal.pageSize.getWidth();
    const contentWidth = pageWidth - margin * 2;
    let y = 136;

    const ensureSpace = (needed = 70) => {
      if (y + needed <= 785) return;
      footer();
      pdf.addPage();
      header(pdf.getNumberOfPages());
      y = 136;
    };

    const writeParagraph = (text, options = {}) => {
      pdf.setTextColor(20, 25, 32);
      pdf.setFont("helvetica", options.bold ? "bold" : "normal");
      pdf.setFontSize(options.size || 9.2);
      const lines = pdf.splitTextToSize(String(text || ""), options.width || contentWidth);
      ensureSpace(lines.length * 14 + 12);
      pdf.text(lines, options.x || margin, y, {
        align: options.align || "left",
        maxWidth: options.width || contentWidth,
        lineHeightFactor: 1.45,
      });
      y += lines.length * 14 + (options.after ?? 10);
    };

    const detailRow = (label, value) => {
      const labelWidth = 150;
      const lines = pdf.splitTextToSize(String(value || "—"), contentWidth - labelWidth - 18);
      const rowHeight = Math.max(27, lines.length * 11 + 10);
      ensureSpace(rowHeight + 2);
      pdf.setDrawColor(...border);
      pdf.setFillColor(244, 244, 244);
      pdf.rect(margin, y, labelWidth, rowHeight, "FD");
      pdf.rect(margin + labelWidth, y, contentWidth - labelWidth, rowHeight);
      pdf.setTextColor(45, 50, 57);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(7.6);
      pdf.text(label, margin + 8, y + 17);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.text(lines, margin + labelWidth + 9, y + 16);
      y += rowHeight;
    };

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(20, 25, 32);
    pdf.text(`Date: ${formatDate(r.createdAt)}`, pageWidth - margin, y, { align: "right" });
    y += 20;
    writeParagraph("To,", { bold: true, after: 4 });
    writeParagraph(`The District Registrar, Cooperative Societies,\n${r.district || "Concerned District"}, ${r.state || ""}`, { after: 15 });

    writeParagraph(
      `Subject: Request for DCDC permission/approval for construction of ${r.capacityMt || "____"} MT grain storage godown under the World's Largest Grain Storage Plan in the Cooperative Sector.`,
      { bold: true, after: 12 }
    );
    writeParagraph(`Reference: PACS Application ID ${r.applicationId || r.id}`, { bold: true, after: 14 });
    writeParagraph("Respected Sir/Madam,", { bold: true, after: 8 });
    writeParagraph(
      `We, ${r.pacsName || "the undersigned PACS/Society"}, Registration No. ${r.pacsRegistrationNo || "________"}, situated at ${[r.village, r.taluka, r.district, r.state].filter(Boolean).join(", ")}, hereby submit that our PACS/Society intends to construct a ${r.capacityMt || "____"} MT grain storage godown under the World's Largest Grain Storage Plan in the Cooperative Sector.`
    );
    writeParagraph(
      `For implementation of the proposed project, the PACS/Society desires to enter into the prescribed Memorandum of Understanding with NAFED and to undertake the project through the designated Project Management Consultant and other competent stakeholders, subject to applicable scheme guidelines, technical appraisal, financing approval and statutory requirements.`
    );
    writeParagraph(
      `We therefore request your office to kindly scrutinise the proposal, place the matter before the District Cooperative Development Committee (DCDC), and grant/communicate the necessary permission and approval for further processing and construction of the proposed grain storage godown.`
    );

    writeParagraph("Brief particulars of the proposal are given below:", { bold: true, after: 7 });
    detailRow("PACS / Society Name", r.pacsName);
    detailRow("Registration Number", r.pacsRegistrationNo);
    detailRow("Managing Committee Resolution", [r.resolutionNumber, formatDate(r.resolutionDate)].filter((value) => value && value !== "—").join(" dated "));
    detailRow("Proposed Capacity", r.capacityMt ? `${r.capacityMt} MT` : "—");
    detailRow("Project Location", [r.village, r.taluka, r.district, r.state].filter(Boolean).join(", "));
    detailRow("Godown / Structure Type", r.godownType);
    detailRow("Available Land Area", r.landArea ? `${r.landArea} sq. m.` : "As per enclosed land record");
    detailRow("Land Ownership / Survey No.", [r.landOwnership || r.landOwnershipType, r.landSurveyNumber || r.surveyNumber].filter(Boolean).join(" / "));
    detailRow("Authorised Contact", [r.contactName, r.contactPhone, r.contactEmail].filter(Boolean).join(" / "));
    y += 13;

    writeParagraph(
      "The PACS/Society undertakes to furnish original records whenever required, comply with the decision of DCDC and other competent authorities, arrange its prescribed contribution/margin money, and fulfil the applicable conditions for implementation of the project.",
      { after: 12 }
    );
    writeParagraph("We request favourable consideration and necessary approval.", { after: 15 });
    writeParagraph("Yours faithfully,", { bold: true, after: 8 });

    ensureSpace(110);
    const signWidth = (contentWidth - 12) / 2;
    pdf.setDrawColor(...border);
    pdf.rect(margin, y, signWidth, 85);
    pdf.rect(margin + signWidth + 12, y, signWidth, 85);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    pdf.text("CHAIRMAN", margin + 9, y + 16);
    pdf.text("SECRETARY / CHIEF EXECUTIVE OFFICER", margin + signWidth + 21, y + 16);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7.3);
    pdf.text("Signature: __________________________", margin + 9, y + 51);
    pdf.text("Name: _____________________________", margin + 9, y + 68);
    pdf.text("Signature: __________________________", margin + signWidth + 21, y + 51);
    pdf.text("Name: _____________________________", margin + signWidth + 21, y + 68);
    y += 98;

    const documentNames = [];
    const addDocumentName = (value) => {
      const name = typeof value === "string"
        ? value
        : value?.name || value?.title || value?.fileName || value?.code;
      if (name && !documentNames.some((existing) => existing.toLowerCase() === String(name).toLowerCase())) {
        documentNames.push(String(name));
      }
    };
    if (Array.isArray(r.documents)) r.documents.forEach(addDocumentName);
    else if (r.documents && typeof r.documents === "object") Object.entries(r.documents).forEach(([key, value]) => addDocumentName(value || key));
    if (Array.isArray(r.additionalDocuments)) r.additionalDocuments.forEach(addDocumentName);
    if (!documentNames.length) {
      [
        "PACS / Society Registration Certificate",
        "Registered Bye-laws",
        "Managing Committee Resolution",
        "Land Ownership / Registered Lease Document",
        "Latest Audit Report",
        "Financial Statements for the prescribed period",
      ].forEach(addDocumentName);
    }

    ensureSpace(55 + documentNames.length * 15);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.text("Enclosures:", margin, y);
    y += 15;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8.2);
    documentNames.forEach((name, index) => {
      const lines = pdf.splitTextToSize(`${index + 1}. ${name}`, contentWidth - 10);
      ensureSpace(lines.length * 12 + 3);
      pdf.text(lines, margin + 5, y);
      y += lines.length * 12 + 3;
    });

    ensureSpace(56);
    y += 8;
    pdf.setDrawColor(...border);
    pdf.rect(pageWidth - margin - 112, y, 112, 48);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7.2);
    pdf.text("OFFICIAL PACS SEAL", pageWidth - margin - 56, y + 27, { align: "center" });
    footer();
  } else {
    const headers = ["#", "Application ID", "PACS / Contact", "Location", "Capacity", "Submitted", "Site Visit", "PMC Decision"];
    const widths = [24, 100, 170, 130, 65, 75, 70, 100];
    let y = 92;
    const drawTableHeader = () => {
      let x = margin;
      pdf.setFillColor(232, 233, 235);
      pdf.setDrawColor(...border);
      pdf.setTextColor(...navy);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(7.2);
      headers.forEach((value, index) => {
        pdf.rect(x, y, widths[index], 27, "FD");
        pdf.text(value, x + 5, y + 17);
        x += widths[index];
      });
      y += 27;
    };
    header(1);
    pdf.setTextColor(70, 84, 102);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.text(`Total Records: ${list.length}`, margin, 82);
    drawTableHeader();
    list.forEach((r, index) => {
      const values = [
        String(index + 1),
        r.applicationId || r.id || "—",
        `${r.pacsName || "—"}\n${r.contactName || "—"} | ${r.contactPhone || "—"}`,
        `${r.district || "—"}, ${r.state || "—"}\n${r.taluka || "—"} / ${r.village || "—"}`,
        r.capacityMt ? `${r.capacityMt} MT` : "—",
        formatDate(r.createdAt),
        r.siteVisitStatus || "NEW",
        r.pmcDecision || "PENDING",
      ];
      const wrapped = values.map((value, i) => pdf.splitTextToSize(String(value), widths[i] - 10));
      const rowHeight = Math.max(31, ...wrapped.map((lines) => lines.length * 10 + 10));
      if (y + rowHeight > 540) {
        footer();
        pdf.addPage();
        header(pdf.getNumberOfPages());
        y = 92;
        drawTableHeader();
      }
      let x = margin;
      wrapped.forEach((lines, i) => {
        pdf.setDrawColor(...border);
        pdf.setFillColor(index % 2 ? 247 : 255, index % 2 ? 247 : 255, index % 2 ? 247 : 255);
        pdf.rect(x, y, widths[i], rowHeight, "FD");
        pdf.setTextColor(25, 39, 56);
        pdf.setFont("helvetica", i === 1 || i === 2 ? "bold" : "normal");
        pdf.setFontSize(7);
        pdf.text(lines, x + 5, y + 13);
        x += widths[i];
      });
      y += rowHeight;
    });
    footer();
  }

  const totalPages = pdf.getNumberOfPages();
  for (let pageNo = 1; pageNo <= totalPages; pageNo += 1) {
    pdf.setPage(pageNo);
    header(pageNo);
    footer();
  }
  pdf.save(single ? `DCDC_Approval_Request_${safePdfName(list[0].applicationId || list[0].id)}.pdf` : "NAFED_PACS_Applications_Report.pdf");
}

function safePdfName(value = "Application") {
  return String(value).replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 70);
}

function exportExcelCsv(rows) {
  const headers = [
    "Sr",
    "Application ID",
    "PACS Name",
    "Registration No",
    "State",
    "District",
    "Taluka",
    "Village",
    "Mobile",
    "Capacity MT",
    "Site Visit",
    "PMC Decision",
    "Created Date",
  ];

  const data = rows.map((r, i) => [
    i + 1,
    r.applicationId || r.id || "",
    r.pacsName || "",
    r.pacsRegistrationNo || "",
    r.state || "",
    r.district || "",
    r.taluka || "",
    r.village || "",
    r.contactPhone || "",
    r.capacityMt || "",
    r.siteVisitStatus || "NEW",
    r.pmcDecision || "PENDING",
    formatDate(r.createdAt),
  ]);

  const csv = [headers, ...data]
    .map((row) =>
      row
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(",")
    )
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = "NAFED_PACS_Applications.csv";
  a.click();

  URL.revokeObjectURL(url);
}
export default function PmcPacsApplicationsPane({ nafedScope }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [stateFilter, setStateFilter] = useState("all");
  const [districtFilter, setDistrictFilter] = useState("all");
  const [talukaFilter, setTalukaFilter] = useState("all");
  const [decisionFilter, setDecisionFilter] = useState("all");
  const [visitFilter, setVisitFilter] = useState("all");
  const [capacityFilter, setCapacityFilter] = useState("all");
  const [search, setSearch] = useState("");

  const [sortKey, setSortKey] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");

  const [page, setPage] = useState(1);
  const pageSize = 15;

  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);

      const snap = await getDocs(collection(db, "godown_projects"));

      const list = snap.docs
        .map((d) => normalizeRow({ id: d.id, ...d.data() }))
        .filter((r) => applyNafedScopeFilter([r], nafedScope).length > 0)
        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

      setRows(list);
    } catch (e) {
      console.error(e);
      toast.error("NAFED PACS applications loading failed.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [JSON.stringify(nafedScope || {})]);

  const states = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => r.state && set.add(r.state));
    return ["all", ...Array.from(set).sort()];
  }, [rows]);

  const districts = useMemo(() => {
    const set = new Set();
    rows
      .filter((r) => stateFilter === "all" || r.state === stateFilter)
      .forEach((r) => r.district && set.add(r.district));
    return ["all", ...Array.from(set).sort()];
  }, [rows, stateFilter]);

  const talukas = useMemo(() => {
    const set = new Set();
    rows
      .filter((r) => stateFilter === "all" || r.state === stateFilter)
      .filter((r) => districtFilter === "all" || r.district === districtFilter)
      .forEach((r) => r.taluka && set.add(r.taluka));
    return ["all", ...Array.from(set).sort()];
  }, [rows, stateFilter, districtFilter]);

  const filteredRows = useMemo(() => {
    let list = [...rows];

    if (stateFilter !== "all") {
      list = list.filter((r) => r.state === stateFilter);
    }

    if (districtFilter !== "all") {
      list = list.filter((r) => r.district === districtFilter);
    }

    if (talukaFilter !== "all") {
      list = list.filter((r) => r.taluka === talukaFilter);
    }

    if (decisionFilter !== "all") {
      list = list.filter((r) => (r.pmcDecision || "PENDING") === decisionFilter);
    }

    if (visitFilter !== "all") {
      list = list.filter((r) => (r.siteVisitStatus || "NEW") === visitFilter);
    }

    if (capacityFilter !== "all") {
      list = list.filter((r) => String(r.capacityMt || "") === capacityFilter);
    }

    const needle = search.trim().toLowerCase();

    if (needle) {
      list = list.filter((r) => {
        const hay = [
          r.applicationId,
          r.pacsName,
          r.pacsRegistrationNo,
          r.contactName,
          r.contactPhone,
          r.contactEmail,
          r.state,
          r.district,
          r.taluka,
          r.village,
          r.capacityMt,
        ]
          .map((x) => String(x || "").toLowerCase())
          .join(" | ");

        return hay.includes(needle);
      });
    }

    list.sort((a, b) => {
      let av = a[sortKey];
      let bv = b[sortKey];

      if (sortKey === "createdAt") {
        av = a.createdAt?.seconds || 0;
        bv = b.createdAt?.seconds || 0;
      }

      if (sortKey === "capacityMt") {
        av = Number(a.capacityMt || 0);
        bv = Number(b.capacityMt || 0);
      }

      av = typeof av === "string" ? av.toLowerCase() : av;
      bv = typeof bv === "string" ? bv.toLowerCase() : bv;

      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

    return list;
  }, [
    rows,
    stateFilter,
    districtFilter,
    talukaFilter,
    decisionFilter,
    visitFilter,
    capacityFilter,
    search,
    sortKey,
    sortDir,
  ]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page]);

  useEffect(() => {
    setPage(1);
  }, [
    stateFilter,
    districtFilter,
    talukaFilter,
    decisionFilter,
    visitFilter,
    capacityFilter,
    search,
  ]);

  const stats = useMemo(() => {
    return {
      total: rows.length,
      pending: rows.filter((r) => (r.pmcDecision || "PENDING") === "PENDING").length,
      approved: rows.filter((r) => r.pmcDecision === "APPROVED_TO_WORKFLOW").length,
      rejected: rows.filter((r) => r.pmcDecision === "REJECTED").length,
    };
  }, [rows]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const openEdit = (row) => {
    setEditing(row);

    setEditForm({
      pacsName: row.pacsName || "",
      pacsRegistrationNo: row.pacsRegistrationNo || "",
      pacsType: row.pacsType || "PACS",
      societyAddress: row.societyAddress || row.address || "",
      resolutionNumber: row.resolutionNumber || "",
      resolutionDate: row.resolutionDate || "",

      contactName: row.contactName || "",
      contactPhone: row.contactPhone || "",
      contactEmail: row.contactEmail || "",

      state: row.state || "",
      district: row.district || "",
      taluka: row.taluka || "",
      village: row.village || "",

      capacityMt: row.capacityMt || "",
      godownType: row.godownType || "",

      plotLength: row.plotLength || "",
      plotWidth: row.plotWidth || "",
      landArea: row.landArea || "",
      landDetails: row.landDetails || "",
      landOwnership: row.landOwnership || row.landOwnershipType || "OWNED",
      landSurveyNumber: row.landSurveyNumber || row.surveyNumber || "",
      googleMapUrl: row.googleMapUrl || "",

      siteVisitStatus: row.siteVisitStatus || "NEW",
      siteVisitReportText: row.siteVisitReportText || "",

      pmcDecision: row.pmcDecision || "PENDING",
      pmcDecisionRemark: row.pmcDecisionRemark || "",
    });
  };

  const closeEdit = () => {
    if (saving) return;
    setEditing(null);
    setEditForm(null);
  };

  useEffect(() => {
    if (!editing) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeOnEscape = (event) => {
      if (event.key === "Escape" && !saving) {
        setEditing(null);
        setEditForm(null);
      }
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [editing, saving]);

  const updateEditField = (field, value) => {
    setEditForm((prev) => {
      const next = { ...(prev || {}), [field]: value };

      if (field === "capacityMt" && value) {
        next.godownType = `${value} MT Godown`;
      }

      if (field === "plotLength" || field === "plotWidth") {
        const length =
          field === "plotLength" ? Number(value) || 0 : Number(next.plotLength) || 0;
        const width =
          field === "plotWidth" ? Number(value) || 0 : Number(next.plotWidth) || 0;
        next.landArea = length * width;
      }

      return next;
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();

    if (!editing || !editForm) return;

    if (!editForm.pacsName || !editForm.contactPhone) {
      toast.error("PACS name and mobile required.");
      return;
    }

    if (String(editForm.contactPhone).length !== 10) {
      toast.error("Mobile number must be 10 digits.");
      return;
    }

    if (
      editForm.contactEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(editForm.contactEmail).trim())
    ) {
      toast.error("Enter a valid email address.");
      return;
    }

    if (!editForm.state || !editForm.district || !editForm.taluka || !editForm.village) {
      toast.error("State, district, taluka/block and village are required.");
      return;
    }

    if (!(Number(editForm.capacityMt) > 0)) {
      toast.error("Select a valid godown capacity.");
      return;
    }

    try {
      setSaving(true);
      toast.loading("Saving application...");

      const payload = {
        pacsName: String(editForm.pacsName || "").trim(),
        societyName: String(editForm.pacsName || "").trim(),
        organizationName: String(editForm.pacsName || "").trim(),
        pacsRegistrationNo: String(editForm.pacsRegistrationNo || "").trim(),
        registrationNumber: String(editForm.pacsRegistrationNo || "").trim(),
        pacsType: editForm.pacsType || "PACS",
        societyAddress: String(editForm.societyAddress || "").trim(),
        address: String(editForm.societyAddress || "").trim(),
        resolutionNumber: String(editForm.resolutionNumber || "").trim(),
        resolutionDate: editForm.resolutionDate || "",

        contactName: String(editForm.contactName || "").trim(),
        contactPhone: String(editForm.contactPhone || "").trim(),
        mobile: String(editForm.contactPhone || "").trim(),
        contactEmail: String(editForm.contactEmail || "").trim(),
        email: String(editForm.contactEmail || "").trim(),

        state: String(editForm.state || "").trim(),
        district: String(editForm.district || "").trim(),
        taluka: String(editForm.taluka || "").trim(),
        village: String(editForm.village || "").trim(),

        capacityMt: Number(editForm.capacityMt) || null,
        proposedCapacityMt: Number(editForm.capacityMt) || null,
        godownType:
          editForm.godownType ||
          (editForm.capacityMt ? `${editForm.capacityMt} MT Godown` : ""),

        plotLength: Number(editForm.plotLength) || 0,
        plotWidth: Number(editForm.plotWidth) || 0,
        landArea:
          Number(editForm.landArea) ||
          (Number(editForm.plotLength) || 0) *
            (Number(editForm.plotWidth) || 0),
        landAreaSqM:
          Number(editForm.landArea) ||
          (Number(editForm.plotLength) || 0) *
            (Number(editForm.plotWidth) || 0),

        landDetails: String(editForm.landDetails || "").trim(),
        landOwnership: editForm.landOwnership || "OWNED",
        landOwnershipType: editForm.landOwnership || "OWNED",
        landSurveyNumber: String(editForm.landSurveyNumber || "").trim(),
        googleMapUrl: String(editForm.googleMapUrl || "").trim(),

        siteVisitStatus: editForm.siteVisitStatus || "NEW",
        siteVisitReportText: String(editForm.siteVisitReportText || "").trim(),

        pmcDecision: editForm.pmcDecision || "PENDING",
        pmcDecisionRemark: String(editForm.pmcDecisionRemark || "").trim(),

        updatedAt: serverTimestamp(),
      };

      await updateDoc(doc(db, "godown_projects", editing.id), payload);

      setRows((prev) =>
        prev.map((r) =>
          r.id === editing.id ? normalizeRow({ ...r, ...payload }) : r
        )
      );

      toast.dismiss();
      toast.success("Application saved.");
      // Do not call closeEdit() here because saving is still true until finally.
      setEditing(null);
      setEditForm(null);
    } catch (e2) {
      console.error(e2);
      toast.dismiss();
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const deleteApplication = async (row) => {
    if (!row?.id) return;

    const linkedModules = [
      row.mouStage && "MOU",
      (row.tenderId || row.generatedTenderId) && "Tender",
      row.loaPdfUrl && "LOA",
      row.contractorId && "Contractor",
      row.workStartedAt && "Construction",
    ].filter(Boolean);

    const warning = linkedModules.length
      ? `This application is linked with ${linkedModules.join(", ")}. It will be removed from the active PMC list but preserved in the audit archive so connected modules do not break.`
      : "The application will be removed from the active PMC list and preserved in the audit archive.";

    if (!window.confirm(`${warning}\n\nContinue with audited deletion?`)) return;

    const reason = window.prompt(
      "Enter deletion/archive reason (minimum 5 characters):",
      "Duplicate or incorrect application"
    );

    if (!reason || reason.trim().length < 5) {
      toast.error("Deletion reason is required.");
      return;
    }

    try {
      toast.loading("Removing application from active list...", { id: "delete-pacs" });
      const batch = writeBatch(db);
      const projectRef = doc(db, "godown_projects", row.id);
      const activityRef = doc(collection(db, "nafed_activity"));

      batch.update(projectRef, {
        isDeleted: true,
        archived: true,
        archiveStatus: "DELETED_FROM_PMC_ACTIVE_LIST",
        deletionReason: reason.trim(),
        deletedByUid: nafedScope?.pmcUid || "",
        deletedByEmail: nafedScope?.pmcEmail || "",
        deletedByRole: "PMC",
        deletedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      batch.set(activityRef, {
        action: "NAFED_PACS_APPLICATION_ARCHIVED",
        entityType: "GODOWN_PROJECT",
        entityId: row.id,
        projectId: row.id,
        applicationId: row.applicationId || row.id,
        pacsName: row.pacsName || "",
        state: row.state || "",
        district: row.district || "",
        reason: reason.trim(),
        linkedModules,
        performedByUid: nafedScope?.pmcUid || "",
        performedByEmail: nafedScope?.pmcEmail || "",
        performedByRole: "PMC",
        createdAt: serverTimestamp(),
      });

      await batch.commit();
      setRows((current) => current.filter((item) => item.id !== row.id));
      if (selected?.id === row.id) setSelected(null);
      if (editing?.id === row.id) closeEdit();
      toast.success("Application removed from active list and saved in audit archive.", { id: "delete-pacs" });
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Application could not be removed. Check Firestore update permission.", { id: "delete-pacs" });
    }
  };
    const renderDecisionBadge = (value) => {
    const v = value || "PENDING";

    if (v === "APPROVED_TO_WORKFLOW") {
      return <Badge type="approved">APPROVED</Badge>;
    }

    if (v === "REJECTED") {
      return <Badge type="rejected">REJECTED</Badge>;
    }

    return <Badge type="pending">PENDING</Badge>;
  };

  const renderVisitBadge = (value) => {
    const v = value || "NEW";

    if (v === "DONE") {
      return <Badge type="approved">DONE</Badge>;
    }

    if (v === "PLANNED") {
      return <Badge type="info">PLANNED</Badge>;
    }

    return <Badge type="pending">NEW</Badge>;
  };

  return (
    <div className="nafed-pacs-pane">
      <style>{`
        .nafed-pacs-pane {
          display: grid;
          gap: 16px;
          font-family: Inter, "Segoe UI", Arial, sans-serif;
        }

        .np-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
        }

        .stat-card {
          background: white;
          border: 1px solid #dbe4ef;
          border-radius: 16px;
          padding: 16px;
          box-shadow: 0 8px 22px rgba(15,23,42,.05);
        }

        .stat-card label {
          display: block;
          font-size: 10px;
          color: #64748b;
          font-weight: 950;
          text-transform: uppercase;
          letter-spacing: .12em;
        }

        .stat-card b {
          display: block;
          margin-top: 8px;
          color: #071f45;
          font-size: 26px;
          font-weight: 950;
        }

        .np-toolbar {
          background: white;
          border: 1px solid #dbe4ef;
          border-radius: 16px;
          padding: 14px;
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 10px;
          box-shadow: 0 8px 22px rgba(15,23,42,.05);
        }

        .np-toolbar label {
          display: block;
          font-size: 10px;
          color: #64748b;
          font-weight: 950;
          text-transform: uppercase;
          letter-spacing: .08em;
          margin-bottom: 5px;
        }

        .np-input {
          width: 100%;
          height: 38px;
          border: 1px solid #cbd5e1;
          border-radius: 10px;
          padding: 0 10px;
          font-size: 12px;
          font-weight: 800;
          outline: none;
          background: white;
          color: #071f45;
        }

        .np-search {
          grid-column: span 2;
        }

        .np-actions {
          background: white;
          border: 1px solid #dbe4ef;
          border-radius: 16px;
          padding: 12px 14px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          box-shadow: 0 8px 22px rgba(15,23,42,.05);
        }

        .np-actions-left {
          color: #475569;
          font-size: 12px;
          font-weight: 800;
        }

        .np-actions-right {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .np-btn {
          border: 0;
          border-radius: 10px;
          padding: 9px 12px;
          font-size: 12px;
          font-weight: 950;
          cursor: pointer;
        }

        .np-btn.primary {
          background: #062b55;
          color: white;
        }

        .np-btn.green {
          background: #16a34a;
          color: white;
        }

        .np-btn.gray {
          background: #e2e8f0;
          color: #071f45;
          border: 1px solid #cbd5e1;
        }

        .np-btn.red {
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #fecaca;
        }

        .np-table-card {
          background: white;
          border: 1.5px solid #111827;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 8px 22px rgba(15,23,42,.05);
        }

        .np-table-head {
          padding: 14px 16px;
          border-bottom: 1.5px solid #111827;
          background: #f8fafc;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
        }

        .np-table-head h3 {
          margin: 0;
          color: #071f45;
          font-size: 17px;
          font-weight: 950;
        }

        .np-table-head span {
          color: #64748b;
          font-size: 12px;
          font-weight: 900;
        }

        .np-table-wrap {
          overflow-x: auto;
          overflow-y: visible;
          width: 100%;
          scrollbar-color: #64748b #e2e8f0;
          scrollbar-width: thin;
        }

        .np-table {
          width: 100%;
          min-width: 1180px;
          table-layout: fixed;
          border-collapse: collapse;
          font-size: 11px;
        }

        .np-table th {
          background: #e2e8f0;
          color: #071f45;
          text-align: left;
          padding: 10px 8px;
          border: 1.5px solid #111827;
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: .08em;
          font-weight: 950;
          white-space: nowrap;
          cursor: pointer;
          position: sticky;
          top: 0;
          z-index: 2;
        }

        .np-table td {
          padding: 10px 8px;
          border: 1.5px solid #111827;
          vertical-align: top;
          background: white;
          color: #0f172a;
          font-weight: 750;
          overflow-wrap: anywhere;
          word-break: normal;
        }

        .np-table th:first-child,
        .np-table td:first-child {
          position: sticky;
          left: 0;
          z-index: 3;
          box-shadow: 2px 0 0 #cbd5e1;
        }

        .np-table th:last-child,
        .np-table td:last-child {
          position: sticky;
          right: 0;
          z-index: 3;
          box-shadow: -2px 0 0 #cbd5e1;
        }

        .np-table th:first-child,
        .np-table th:last-child { z-index: 5; }

        .np-table tbody tr:nth-child(even) td {
          background: #f8fafc;
        }

        .np-table tbody tr:hover td {
          background: #eef6ff;
        }

        .np-title {
          color: #071f45;
          font-weight: 950;
        }

        .muted {
          color: #64748b;
          font-size: 11px;
          line-height: 1.6;
          font-weight: 750;
        }

        .badge {
          border-radius: 999px;
          padding: 6px 10px;
          font-size: 10px;
          font-weight: 950;
          display: inline-block;
          white-space: nowrap;
        }

        .badge.pending {
          background: #fef3c7;
          color: #92400e;
          border: 1px solid #f59e0b;
        }

        .badge.approved {
          background: #dcfce7;
          color: #166534;
          border: 1px solid #16a34a;
        }

        .badge.rejected {
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #ef4444;
        }

        .badge.info {
          background: #dbeafe;
          color: #1e40af;
          border: 1px solid #93c5fd;
        }

        .badge.neutral {
          background: #f1f5f9;
          color: #334155;
          border: 1px solid #cbd5e1;
        }

        .np-row-actions {
          display: grid;
          gap: 6px;
        }

        .np-pagination {
          padding: 12px 16px;
          border-top: 1.5px solid #111827;
          background: #f8fafc;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
        }

        .np-page-buttons {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .np-empty {
          padding: 40px;
          text-align: center;
          color: #64748b;
          font-size: 13px;
          font-weight: 900;
        }

        @media(max-width:1200px) {
          .np-stats {
            grid-template-columns: repeat(2, 1fr);
          }

          .np-toolbar {
            grid-template-columns: repeat(2, 1fr);
          }

          .np-search {
            grid-column: span 2;
          }
        }

        @media(max-width:700px) {
          .np-stats {
            grid-template-columns: 1fr;
          }

          .np-toolbar {
            grid-template-columns: 1fr;
          }

          .np-search {
            grid-column: span 1;
          }

          .np-actions {
            flex-direction: column;
            align-items: flex-start;
          }
        }
      `}</style>

      <section className="np-stats">
        <StatCard label="NAFED Assigned Applications" value={stats.total} />
        <StatCard label="Pending PMC" value={stats.pending} />
        <StatCard label="Approved To Workflow" value={stats.approved} />
        <StatCard label="Rejected" value={stats.rejected} />
      </section>

      <section className="np-toolbar">
        <div>
          <label>State</label>
          <select
            className="np-input"
            value={stateFilter}
            onChange={(e) => {
              setStateFilter(e.target.value);
              setDistrictFilter("all");
              setTalukaFilter("all");
            }}
          >
            {states.map((s) => (
              <option key={s} value={s}>
                {s === "all" ? "All States" : s}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label>District</label>
          <select
            className="np-input"
            value={districtFilter}
            onChange={(e) => {
              setDistrictFilter(e.target.value);
              setTalukaFilter("all");
            }}
          >
            {districts.map((d) => (
              <option key={d} value={d}>
                {d === "all" ? "All Districts" : d}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label>Taluka / Block</label>
          <select
            className="np-input"
            value={talukaFilter}
            onChange={(e) => setTalukaFilter(e.target.value)}
          >
            {talukas.map((t) => (
              <option key={t} value={t}>
                {t === "all" ? "All Talukas" : t}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label>PMC Decision</label>
          <select
            className="np-input"
            value={decisionFilter}
            onChange={(e) => setDecisionFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="PENDING">Pending</option>
            <option value="APPROVED_TO_WORKFLOW">Approved</option>
            <option value="REJECTED">Rejected</option>
          </select>
        </div>

        <div>
          <label>Site Visit</label>
          <select
            className="np-input"
            value={visitFilter}
            onChange={(e) => setVisitFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="NEW">New</option>
            <option value="PLANNED">Planned</option>
            <option value="DONE">Done</option>
          </select>
        </div>

        <div>
          <label>Capacity</label>
          <select
            className="np-input"
            value={capacityFilter}
            onChange={(e) => setCapacityFilter(e.target.value)}
          >
            <option value="all">All</option>
            {CAPACITY_OPTIONS.map((c) => (
              <option key={c} value={String(c)}>
                {c} MT
              </option>
            ))}
          </select>
        </div>

        <div className="np-search">
          <label>Search</label>
          <input
            className="np-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Application ID / PACS / mobile / village / district..."
          />
        </div>
      </section>

      <section className="np-actions">
        <div className="np-actions-left">
          Showing <b>{filteredRows.length}</b> applications from allocated
          district scope only.
        </div>

        <div className="np-actions-right">
          <button className="np-btn gray" onClick={load}>
            Refresh
          </button>

          <button
            className="np-btn green"
            onClick={() => exportExcelCsv(filteredRows)}
          >
            Excel / CSV
          </button>

          <button
            className="np-btn primary"
            onClick={() =>
              downloadPdfReport(
                filteredRows,
                "NAFED Assigned PACS Applications",
                "REPORT"
              )
            }
          >
            PDF Report
          </button>

          <button className="np-btn gray" onClick={() => window.print()}>
            Print
          </button>
        </div>
      </section>

      <section className="np-table-card">
        <div className="np-table-head">
          <h3>NAFED Assigned PACS Applications</h3>
          <span>
            Page {page} of {totalPages}
          </span>
        </div>

        {loading ? (
          <div className="np-empty">Loading applications...</div>
        ) : filteredRows.length === 0 ? (
          <div className="np-empty">
            No application found for this PMC allocated districts.
          </div>
        ) : (
          <>
            <div className="np-table-wrap">
              <table className="np-table">
                <colgroup>
                  <col style={{ width: "145px" }} />
                  <col style={{ width: "190px" }} />
                  <col style={{ width: "90px" }} />
                  <col style={{ width: "120px" }} />
                  <col style={{ width: "135px" }} />
                  <col style={{ width: "90px" }} />
                  <col style={{ width: "115px" }} />
                  <col style={{ width: "95px" }} />
                  <col style={{ width: "120px" }} />
                  <col style={{ width: "110px" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th onClick={() => toggleSort("applicationId")}>Application</th>
                    <th onClick={() => toggleSort("pacsName")}>PACS / Contact</th>
                    <th onClick={() => toggleSort("state")}>State</th>
                    <th onClick={() => toggleSort("district")}>District</th>
                    <th onClick={() => toggleSort("taluka")}>Taluka / Village</th>
                    <th onClick={() => toggleSort("capacityMt")}>Capacity</th>
                    <th onClick={() => toggleSort("createdAt")}>Created</th>
                    <th>Site Visit</th>
                    <th>PMC Decision</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {pagedRows.map((r, index) => (
                    <tr key={r.id}>
                      <td>
                        <div className="np-title">
                          {(page - 1) * pageSize + index + 1}.
                        </div>
                        <div className="muted" style={{ color: "#0758aa", fontWeight: 900 }}>
                          {r.applicationId || r.id}
                        </div>
                        <div className="muted">NAFED Apply Form</div>
                      </td>

                      <td>
                        <div className="np-title">{r.pacsName || "—"}</div>
                        <div className="muted">
                          Reg: {r.pacsRegistrationNo || "—"}
                        </div>
                        <div className="muted">
                          {r.contactName || "—"} | {r.contactPhone || "—"}
                        </div>
                      </td>

                      <td>{r.state || "—"}</td>

                      <td>
                        <div className="np-title">{r.district || "—"}</div>
                      </td>

                      <td>
                        <div>{r.taluka || "—"}</div>
                        <div className="muted">{r.village || "—"}</div>
                      </td>

                      <td>
                        <div className="np-title">
                          {r.capacityMt || "—"} MT
                        </div>
                        <div className="muted">{r.godownType || ""}</div>
                      </td>

                      <td>
                        <div>{formatDate(r.createdAt)}</div>
                        <div className="muted">{formatDateTime(r.updatedAt)}</div>
                      </td>

                      <td>{renderVisitBadge(r.siteVisitStatus)}</td>

                      <td>
                        {renderDecisionBadge(r.pmcDecision)}
                        {r.pmcDecisionRemark && (
                          <div className="muted">{r.pmcDecisionRemark}</div>
                        )}
                      </td>

                      <td>
                        <div className="np-row-actions">
                          <button
                            className="np-btn gray"
                            onClick={() => setSelected(r)}
                          >
                            View
                          </button>

                          <button
                            className="np-btn primary"
                            onClick={() => openEdit(r)}
                          >
                            Edit
                          </button>

                          <button
                            className="np-btn green"
                            onClick={() =>
                              downloadPdfReport(
                                [r],
                                "Request for DCDC Approval — Grain Storage Godown",
                                "LETTER"
                              )
                            }
                          >
                            PDF
                          </button>

                          <button
                            className="np-btn red"
                            onClick={() => deleteApplication(r)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="np-pagination">
              <div>
                Total: <b>{filteredRows.length}</b> Records
              </div>

              <div className="np-page-buttons">
                <button
                  className="np-btn gray"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </button>

                <span>
                  {page} / {totalPages}
                </span>

                <button
                  className="np-btn gray"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </section>
            {selected && (
        <div className="modal-backdrop" onClick={() => setSelected(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3>Application Details</h3>
              <button onClick={() => setSelected(null)}>Close</button>
            </div>

            <div className="modal-body">
              {[
                ["Application ID", selected.applicationId || selected.id],
                ["PACS / Society", selected.pacsName],
                ["Registration No.", selected.pacsRegistrationNo],
                ["Contact Person", selected.contactName],
                ["Mobile", selected.contactPhone],
                ["Email", selected.contactEmail],
                ["State", selected.state],
                ["District", selected.district],
                ["Taluka", selected.taluka],
                ["Village", selected.village],
                ["Capacity", `${selected.capacityMt || "—"} MT`],
                ["Godown Type", selected.godownType],
                ["Site Visit", selected.siteVisitStatus || "NEW"],
                ["PMC Decision", selected.pmcDecision || "PENDING"],
                ["Created", formatDateTime(selected.createdAt)],
                ["Updated", formatDateTime(selected.updatedAt)],
              ].map(([label, value]) => (
                <div className="info-box" key={label}>
                  <label>{label}</label>
                  <b>{value || "—"}</b>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {editing && editForm && createPortal(
        <div className="modal-backdrop edit-backdrop" onClick={closeEdit}>
          <div className="edit-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <div>
                <small>APPLICATION RECORD</small>
                <h3>Edit Application Details</h3>
                <p>{editing.applicationId || editing.id} · {editing.pacsName || "PACS / Society"}</p>
              </div>
              <button type="button" onClick={closeEdit} disabled={saving}>Close ×</button>
            </div>

            <form className="edit-body" onSubmit={saveEdit}>
              <div className="edit-instruction">
                <b>Application Data Correction</b>
                <span>Update only verified information. Every saved change remains connected with the same project ID, documents, workflow, MOU and tender records.</span>
              </div>
              <div className="edit-section">
                <h4><span>01</span> Institution Details</h4>

                <div className="edit-grid">
                  <div>
                    <label>PACS / Society Name <b>*</b></label>
                    <input
                      value={editForm.pacsName}
                      onChange={(e) =>
                        updateEditField("pacsName", e.target.value)
                      }
                    />
                  </div>

                  <div>
                    <label>Registration No.</label>
                    <input
                      value={editForm.pacsRegistrationNo}
                      onChange={(e) =>
                        updateEditField("pacsRegistrationNo", e.target.value)
                      }
                    />
                  </div>

                  <div>
                    <label>Institution Type</label>
                    <select
                      value={editForm.pacsType}
                      onChange={(e) =>
                        updateEditField("pacsType", e.target.value)
                      }
                    >
                      <option value="PACS">PACS</option>
                      <option value="MSCS">Multi-State Cooperative Society</option>
                      <option value="FPC">Farmer Producer Company</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>

                  <div>
                    <label>Managing Committee Resolution No.</label>
                    <input
                      value={editForm.resolutionNumber}
                      onChange={(e) =>
                        updateEditField("resolutionNumber", e.target.value)
                      }
                    />
                  </div>

                  <div>
                    <label>Resolution Date</label>
                    <input
                      type="date"
                      value={editForm.resolutionDate}
                      onChange={(e) =>
                        updateEditField("resolutionDate", e.target.value)
                      }
                    />
                  </div>
                </div>

                <div className="full-field">
                  <label>Registered Address of PACS / Society</label>
                  <textarea
                    rows={2}
                    value={editForm.societyAddress}
                    onChange={(e) =>
                      updateEditField("societyAddress", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="edit-section">
                <h4><span>02</span> Authorised Contact Details</h4>

                <div className="edit-grid">
                  <div>
                    <label>Contact Person</label>
                    <input
                      value={editForm.contactName}
                      onChange={(e) =>
                        updateEditField("contactName", e.target.value)
                      }
                    />
                  </div>

                  <div>
                    <label>Mobile Number <b>*</b></label>
                    <input
                      value={editForm.contactPhone}
                      onChange={(e) =>
                        updateEditField(
                          "contactPhone",
                          e.target.value.replace(/\D/g, "").slice(0, 10)
                        )
                      }
                    />
                  </div>

                  <div>
                    <label>Email</label>
                    <input
                      value={editForm.contactEmail}
                      onChange={(e) =>
                        updateEditField("contactEmail", e.target.value)
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="edit-section">
                <h4><span>03</span> Project Location — All India</h4>

                <div className="edit-grid">
                  <div>
                    <label>State</label>
                    <input
                      value={editForm.state}
                      onChange={(e) =>
                        updateEditField("state", e.target.value)
                      }
                      placeholder="Example: Gujarat / Rajasthan / Maharashtra"
                    />
                  </div>

                  <div>
                    <label>District</label>
                    <input
                      value={editForm.district}
                      onChange={(e) =>
                        updateEditField("district", e.target.value)
                      }
                      placeholder="District"
                    />
                  </div>

                  <div>
                    <label>Taluka / Block</label>
                    <input
                      value={editForm.taluka}
                      onChange={(e) =>
                        updateEditField("taluka", e.target.value)
                      }
                    />
                  </div>

                  <div>
                    <label>Village</label>
                    <input
                      value={editForm.village}
                      onChange={(e) =>
                        updateEditField("village", e.target.value)
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="edit-section">
                <h4><span>04</span> Godown & Land Particulars</h4>

                <div className="edit-grid">
                  <div>
                    <label>Capacity</label>
                    <select
                      value={editForm.capacityMt}
                      onChange={(e) =>
                        updateEditField("capacityMt", e.target.value)
                      }
                    >
                      <option value="">Select Capacity</option>
                      {CAPACITY_OPTIONS.map((mt) => (
                        <option key={mt} value={String(mt)}>
                          {mt} MT
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label>Godown Type</label>
                    <input
                      value={editForm.godownType}
                      onChange={(e) =>
                        updateEditField("godownType", e.target.value)
                      }
                    />
                  </div>

                  <div>
                    <label>Plot Length</label>
                    <input
                      type="number"
                      value={editForm.plotLength}
                      onChange={(e) =>
                        updateEditField("plotLength", e.target.value)
                      }
                    />
                  </div>

                  <div>
                    <label>Plot Width</label>
                    <input
                      type="number"
                      value={editForm.plotWidth}
                      onChange={(e) =>
                        updateEditField("plotWidth", e.target.value)
                      }
                    />
                  </div>

                  <div>
                    <label>Land Area</label>
                    <input value={editForm.landArea || 0} readOnly />
                  </div>

                  <div>
                    <label>Google Map URL</label>
                    <input
                      value={editForm.googleMapUrl}
                      onChange={(e) =>
                        updateEditField("googleMapUrl", e.target.value)
                      }
                    />
                  </div>

                  <div>
                    <label>Land Ownership</label>
                    <select
                      value={editForm.landOwnership}
                      onChange={(e) =>
                        updateEditField("landOwnership", e.target.value)
                      }
                    >
                      <option value="OWNED">Owned by PACS</option>
                      <option value="LEASED">Registered Lease</option>
                      <option value="ALLOTTED">Allotted by Competent Authority</option>
                    </select>
                  </div>

                  <div>
                    <label>Survey / Block / Khasra Number</label>
                    <input
                      value={editForm.landSurveyNumber}
                      onChange={(e) =>
                        updateEditField("landSurveyNumber", e.target.value)
                      }
                    />
                  </div>
                </div>

                <div className="full-field">
                  <label>Land Details</label>
                  <textarea
                    rows={3}
                    value={editForm.landDetails}
                    onChange={(e) =>
                      updateEditField("landDetails", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="edit-section">
                <h4><span>05</span> PMC Processing & Decision</h4>

                <div className="edit-grid">
                  <div>
                    <label>Site Visit Status</label>
                    <select
                      value={editForm.siteVisitStatus}
                      onChange={(e) =>
                        updateEditField("siteVisitStatus", e.target.value)
                      }
                    >
                      <option value="NEW">New</option>
                      <option value="PLANNED">Planned</option>
                      <option value="DONE">Done</option>
                    </select>
                  </div>

                  <div>
                    <label>PMC Decision</label>
                    <select
                      value={editForm.pmcDecision}
                      onChange={(e) =>
                        updateEditField("pmcDecision", e.target.value)
                      }
                    >
                      <option value="PENDING">Pending</option>
                      <option value="APPROVED_TO_WORKFLOW">
                        Approved To Workflow
                      </option>
                      <option value="REJECTED">Rejected</option>
                    </select>
                  </div>
                </div>

                <div className="full-field">
                  <label>Site Visit Report / Remark</label>
                  <textarea
                    rows={2}
                    value={editForm.siteVisitReportText}
                    onChange={(e) =>
                      updateEditField("siteVisitReportText", e.target.value)
                    }
                  />
                </div>

                <div className="full-field">
                  <label>PMC Decision Remark</label>
                  <textarea
                    rows={2}
                    value={editForm.pmcDecisionRemark}
                    onChange={(e) =>
                      updateEditField("pmcDecisionRemark", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="edit-actions">
                <span>Last update: {formatDateTime(editing.updatedAt || editing.createdAt)}</span>
                <button type="button" onClick={closeEdit}>
                  Cancel
                </button>

                <button type="submit" disabled={saving}>
                  {saving ? "Saving..." : "Save Application"}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      <style>{`
        .modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(2, 6, 23, .58);
          display: flex;
          align-items: flex-start;
          justify-content: center;
          z-index: 9999;
          padding: 18px;
          overflow-y: auto;
          overscroll-behavior: contain;
        }

        .modal-card,
        .edit-card {
          width: min(980px, 100%);
          max-height: 94vh;
          overflow: hidden;
          background: white;
          border-radius: 10px;
          box-shadow: 0 30px 90px rgba(0,0,0,.28);
          border: 1px solid #7b8794;
        }

        .edit-card {
          width: 100%;
          height: calc(100vh - 20px);
          max-height: calc(100vh - 20px);
          margin: 0;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          border-radius: 8px;
        }

        .edit-backdrop {
          padding: 10px;
          overflow: hidden;
        }

        .modal-head {
          background: #17212d;
          color: white;
          padding: 16px 22px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: sticky;
          top: 0;
          z-index: 20;
        }

        .modal-head h3 {
          margin: 3px 0;
          font-size: 18px;
          font-weight: 950;
        }

        .modal-head small {
          display: block;
          color: #d1d5db;
          font-size: 9px;
          font-weight: 900;
          letter-spacing: .12em;
        }

        .modal-head p {
          margin: 0;
          color: #d6dde6;
          font-size: 10px;
        }

        .modal-head button {
          border: 0;
          background: rgba(255,255,255,.16);
          color: white;
          border-radius: 5px;
          padding: 8px 12px;
          font-weight: 900;
          cursor: pointer;
        }

        .modal-body {
          padding: 22px;
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 14px;
          max-height: 70vh;
          overflow-y: auto;
        }

        .info-box {
          border: 1px solid #cbd5e1;
          background: #f8fafc;
          border-radius: 14px;
          padding: 14px;
        }

        .info-box label {
          display: block;
          font-size: 10px;
          color: #64748b;
          font-weight: 950;
          text-transform: uppercase;
          letter-spacing: .08em;
          margin-bottom: 6px;
        }

        .info-box b {
          color: #071f45;
          font-size: 13px;
          word-break: break-word;
        }

        .edit-body {
          padding: 18px 20px 20px;
          display: grid;
          gap: 13px;
          min-height: 0;
          flex: 1;
          max-height: none;
          overflow-y: auto;
          overflow-x: hidden;
          scrollbar-gutter: stable;
          background: #eef1f4;
        }

        .edit-instruction {
          display: flex;
          align-items: flex-start;
          gap: 15px;
          padding: 11px 13px;
          border: 1px solid #aeb7c2;
          border-left: 4px solid #24364b;
          background: white;
        }

        .edit-instruction b {
          flex: 0 0 auto;
          color: #17212d;
          font-size: 11px;
        }

        .edit-instruction span {
          color: #526171;
          font-size: 10px;
          line-height: 1.45;
        }

        .edit-section {
          background: white;
          border: 1px solid #cbd5e1;
          border-radius: 7px;
          padding: 15px;
        }

        .edit-section h4 {
          margin: 0 0 14px;
          padding-bottom: 9px;
          border-bottom: 1px solid #c7cfd8;
          color: #17212d;
          font-size: 13px;
          font-weight: 950;
        }

        .edit-section h4 span {
          display: inline-grid;
          place-items: center;
          width: 26px;
          height: 22px;
          margin-right: 8px;
          background: #27384c;
          color: white;
          border-radius: 3px;
          font-size: 9px;
        }

        .edit-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
        }

        .edit-grid label,
        .full-field label {
          display: block;
          font-size: 10px;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: .08em;
          font-weight: 950;
          margin-bottom: 5px;
        }

        .edit-grid label b { color: #b42318; }

        .edit-grid input,
        .edit-grid select,
        .full-field textarea {
          width: 100%;
          border: 1px solid #cbd5e1;
          min-height: 40px;
          border-radius: 5px;
          padding: 9px 10px;
          outline: none;
          font-size: 12px;
          font-weight: 650;
          color: #17212d;
          background: white;
        }

        .edit-grid input:focus,
        .edit-grid select:focus,
        .full-field textarea:focus {
          border-color: #27384c;
          box-shadow: 0 0 0 2px rgba(39,56,76,.12);
        }

        .edit-grid input[readonly] {
          background: #eef1f4;
          color: #5f6b78;
        }

        .full-field {
          margin-top: 12px;
        }

        .edit-actions {
          position: sticky;
          bottom: 0;
          z-index: 15;
          background: white;
          border: 1px solid #cbd5e1;
          border-radius: 7px;
          padding: 14px;
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          margin-top: 2px;
          box-shadow: 0 -5px 16px rgba(15,23,42,.08);
        }

        .edit-actions > span {
          margin-right: auto;
          align-self: center;
          color: #697786;
          font-size: 9px;
          font-weight: 700;
        }

        .edit-actions button {
          border: 0;
          border-radius: 5px;
          padding: 10px 16px;
          font-weight: 950;
          cursor: pointer;
        }

        .edit-actions button:first-child {
          background: #e2e8f0;
          color: #071f45;
        }

        .edit-actions button:last-child {
          background: #17212d;
          color: white;
        }

        @media(max-width:900px) {
          .modal-body,
          .edit-grid {
            grid-template-columns: 1fr;
          }

          .modal-backdrop { padding: 8px; }
          .edit-backdrop { padding: 0; }
          .edit-card { height: 100vh; max-height: 100vh; border-radius: 0; }
          .edit-instruction { display: block; }
          .edit-instruction span { display: block; margin-top: 5px; }
        }
      `}</style>
    </div>
  );
}
